import { DownloadOutlined } from '@ant-design/icons';
import { Col, Modal, Row, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import _timezone from 'dayjs/plugin/timezone';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import { RowTitles } from 'components/molecules/RowTitles';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import fileDownload from 'services/fileDownload';
import kontrolaXMLApi from 'services/korporatniAkce/kontrolaXMLSRD';
import korporatniAkceApi, { IKorporatniAkce } from 'services/korporatniAkce/korporatniAkce';
import stahnoutKorporatniAkciApi from 'services/korporatniAkce/stahnoutKorporatniAkci';
import stahnoutSRDApi from 'services/korporatniAkce/stahnoutSRD';
import { zrusKorporatniAkciZaznamApi } from 'services/korporatniAkce/zrusKorporatniAkci';

import { ETypUdalosti } from 'enums/akcionarKorpAkce/typUdalosti';
import { EkorpAkceButtonType, korpAkceStructure } from 'enums/korporatniAkceTypes';
import routePathEnum from 'enums/routePathEnum';

import { zpusobZadani } from 'data/codebooks/korporatniAkce/zpusobZadani';

import getLanguageObject from 'utils/getLanguageObject';

import { JINAButtons, VHButtons, VYPISButtons } from './DetailButtons';

console.log(_timezone);
let utc = require('dayjs/plugin/utc');
let timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc);
dayjs.extend(timezone);

const tz = 'Europe/Prague';

const rowTitleStyles = {
    maxWidth: '550px',
    marginBottom: '6px',
    fontSize: '18px'
};

const TitleAnt = Typography.Title;

interface IModal {
    open: boolean;
    text: string;
    type: EkorpAkceButtonType | null;
}

const defaultModal: IModal = {
    open: false,
    text: '',
    type: null
};
interface IPotvrditFilelist {
    id: string | number;
    typudalost?: ETypUdalosti;
    datumzverejneni?: string;
    idSRD?: number;
}

interface DetailKorporatniAkceProps {}

export const DetailKorporatniAkce: React.FC<DetailKorporatniAkceProps> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const { aktivniFirma, language } = useContext(Context);
    const { confirm } = Modal;
    const { id } = useParams<{ id: string }>();
    const [data, setData] = useState<IKorporatniAkce>();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingBtn, setLoadingBtn] = useState<boolean>(false);
    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [checking, setChecking] = useState<boolean>(false);
    const [uploadingError, setUploadingError] = useState<boolean>(false);
    const [modal, setModal] = useState<IModal>(defaultModal);
    const [modalFilelist, setModalFilelist] = useState<IFileList[]>([]);
    const [potvrditFilelist, setPotvrditFilelist] = useState<IFileList[]>([]);
    const [modalUploadData, setModalUploadData] = useState<IPotvrditFilelist>();
    const [reload, setReload] = useState<boolean>(false);
    const [typZmeny, setTypZmeny] = useState<string>('');

    useEffect(() => {
        setLoading(true);
        korporatniAkceApi(id!)
            .then(resp => setData(resp))
            .catch(() => navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.KORPORATNI_AKCE}`))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, reload]);

    // nastavenie casovej zony pre tuto page
    useEffect(() => {
        dayjs.tz.setDefault('Europe/Prague');
        return () => {
            dayjs.tz.setDefault();
        };
    }, []);

    const setModalType = (typ: EkorpAkceButtonType) => {
        const text = korpAkceStructure.find(({ type }) => type === typ)?.text;
        setModal({
            open: true,
            text: text ? text : '',
            type: typ
        });
    };

    const showVysledky = (dataType: IKorporatniAkce) => {
        if (dataType.typudalosti === ETypUdalosti.VH) {
            return <VHButtons data={dataType} openModal={val => setModalType(val)} />;
        } else if (dataType.typudalosti === ETypUdalosti.VYPIS) {
            return <VYPISButtons data={dataType} openModal={val => setModalType(val)} />;
        } else if (dataType.typudalosti === ETypUdalosti.JINA) {
            return <JINAButtons data={dataType} openModal={val => setModalType(val)} />;
        }
        return null;
    };
    const downloadVYPISODP = () => {
        setLoadingBtn(val => !val);
        stahnoutKorporatniAkciApi(id!, ETypUdalosti.VYPISODP)
            .then(resp => fileDownload(resp.data.redirect))
            .finally(() => {
                setLoadingBtn(val => !val);
                setModal(defaultModal);
            });
    };
    const downloadVHODP = () => {
        setLoadingBtn(val => !val);
        stahnoutKorporatniAkciApi(id!, ETypUdalosti.VHODP)
            .then(resp => fileDownload(resp.data.redirect))
            .finally(() => {
                setLoadingBtn(val => !val);
                setModal(defaultModal);
            });
    };

    const uploadFile = (buttonType: string) => {
        let type = buttonType.split(/_(?=[^_]*$)/)[1];
        kontrolaXMLApi(
            id!,
            modalUploadData ? potvrditFilelist[0].fileName : modalFilelist[0].fileName,
            modalUploadData ? potvrditFilelist[0].name : modalFilelist[0].name,
            type as ETypUdalosti,
            dayjs().format(),
            // String(data?.datumzverejneni || data?.rozhodnyden),
            modalUploadData && modalUploadData.id
        )
            ?.then(() => {
                setModal(defaultModal);
                setReload(val => !val);
            })
            .catch(() => {
                setUploadingError(true);
            })
            .finally(() => {
                setChecking(false);
            });
    };

    const columns: ColumnsType<{
        key: number;
        date: string;
        datumzverejneni: string;
        type: ETypUdalosti;
        resreq: { akce: ETypUdalosti; datum: Date; typ: ETypUdalosti; id: string; subject: string };
        download: React.ReactNode;
        zrusit: React.ReactNode;
    }> = [
        {
            title: <TranslateText>DATUM</TranslateText>,
            dataIndex: 'date',
            key: 'date',
            render: (text, row) =>
                text +
                (row.datumzverejneni
                    ? // eslint-disable-next-line max-len
                      ` (${getLanguageObject(language).DATUM_UVEREJNENI_SHORT}: ${dayjs(row.datumzverejneni).format('DD.MM.YYYY HH:mm')})`
                    : '')
        },

        {
            title: <TranslateText>TYP</TranslateText>,
            dataIndex: 'type',
            key: 'type',
            render: (type, row) => (
                <>
                    <TranslateText>{`TYPE_${type}`}</TranslateText>
                    {row.resreq.subject ? ` (${row.resreq.subject})` : ''}
                </>
            )
        },
        {
            title: <TranslateText>DORUCENO_ODESLANO</TranslateText>,
            dataIndex: 'resreq',
            key: 'resreq',
            render: (resreq, { key }, index) => (
                <Row>
                    <Col span={12}>
                        {[ETypUdalosti.VHODP, ETypUdalosti.VYPISODP].includes(resreq.akce) ? (
                            <TranslateText>DORUCENO</TranslateText>
                        ) : (
                            <TranslateText>ODESLANO</TranslateText>
                        )}
                    </Col>
                    <Col
                        span={12}
                        style={{
                            textAlign: 'center'
                        }}
                    >
                        {resreq.akce === ETypUdalosti.VHODP && (
                            <AnchorComponent
                                id={`potvrditBtn${index}`}
                                onClick={() => {
                                    setPotvrditFilelist([]);
                                    setModalUploadData({
                                        id: key,
                                        typudalost: resreq.typ,
                                        datumzverejneni: resreq.datum,
                                        idSRD: resreq.id
                                    });
                                    setModal({
                                        open: true,
                                        text: 'SRD_DETAIL_INFO_ZAP',
                                        type: resreq.typ
                                    });
                                }}
                            >
                                POTVRDIT
                            </AnchorComponent>
                        )}
                    </Col>
                </Row>
            )
        },
        {
            title: <TranslateText>STAHNOUT</TranslateText>,
            dataIndex: 'download',
            key: 'download',
            render: download => download,
            align: 'center'
        },
        {
            dataIndex: 'zrusit',
            key: 'zrusit',
            render: (_a, row) =>
                dayjs(row.datumzverejneni) > dayjs() ? (
                    <AnchorComponent
                        onClick={() => {
                            confirm({
                                title:
                                    getLanguageObject(language).ZRUSIT_VH_CONFIRM +
                                    getLanguageObject(language)['TYPE_' + data?.typudalosti],
                                cancelText: <TranslateText>NE</TranslateText>,
                                okText: <TranslateText>ANO</TranslateText>,
                                onOk() {
                                    setLoading(true);
                                    zrusKorporatniAkciZaznamApi(data?.id!, row.key).finally(() => setReload(pv => !pv));
                                }
                            });
                        }}
                    >
                        <TranslateText>ZRUSIT_ZVEREJNENI</TranslateText>
                    </AnchorComponent>
                ) : undefined,
            align: 'center'
        }
    ];

    return (
        <LoadingWrapper
            loading={loading}
            style={{
                width: '100%'
            }}
        >
            {data ? (
                <>
                    <Row justify="start">
                        <TitleAnt level={4}>
                            {data.isin} - {data.nazevemise}(<TranslateText>{data.typudalosti}</TranslateText>)
                        </TitleAnt>
                    </Row>
                    {data?.datumkonani && (
                        <RowTitles
                            firstText="SRD_DLAZDICE_DATUM_KONANI"
                            secondText={dayjs(data?.datumkonani).tz(tz).format('DD.MM.YYYY HH:mm')}
                            paragraph={true}
                            style={rowTitleStyles}
                        />
                    )}
                    {data && !!data.sezakci.length && data.sezakci[0].datumzalozeni && (
                        <RowTitles
                            firstText="DATUM_ZALOZENI"
                            secondText={dayjs(data && data.sezakci[0].datumzalozeni)
                                .tz(tz)
                                .format('DD.MM.YYYY')}
                            paragraph={true}
                            style={rowTitleStyles}
                        />
                    )}
                    {data?.rozhodnyden && (
                        <RowTitles
                            firstText="SRD_DLAZDICE_ROZHODNY_DEN"
                            secondText={dayjs(data?.rozhodnyden).tz(tz).format('DD.MM.YYYY')}
                            paragraph={true}
                            style={rowTitleStyles}
                        />
                    )}
                    {data.vypis && (
                        <RowTitles
                            firstText="SRD_DLAZDICE_VYPIS_AKCIONARU"
                            secondText={data.vypis}
                            paragraph={true}
                            style={rowTitleStyles}
                        />
                    )}
                    <Row
                        style={{
                            paddingBottom: '20px'
                        }}
                    >
                        <Col span={6}>
                            <Paragraph
                                style={{
                                    fontSize: '18px',
                                    maxWidth: '550px'
                                }}
                                text="SRD_DETAIL_VYSLEDEK_VH"
                            />
                        </Col>
                    </Row>
                    {showVysledky(data)}
                    <TableComponent
                        rowKey={e => e.key}
                        columns={columns}
                        dataSource={data.sezakci.map((akce, index) => ({
                            key: akce.id,
                            date: dayjs(akce.datumzalozeni).tz(tz).format('DD.MM.YYYY HH:mm:ss'),
                            datumzverejneni: akce.datumzverejneni,
                            type: akce.typudalosti,
                            resreq: {
                                akce: akce.typudalosti,
                                datum: data.datumkonani,
                                typ: data.typudalosti,
                                subject: akce.subject,
                                id: id!
                            },
                            download: (
                                <Row justify="center" id={`downloadBtn${index}`}>
                                    <DownloadOutlined
                                        onClick={() => {
                                            setLoadingModal(val => !val);
                                            stahnoutSRDApi(akce.tmp)
                                                .then(resp => {
                                                    fileDownload(resp.data.redirect);
                                                })
                                                .finally(() => setLoadingModal(val => !val));
                                        }}
                                    />
                                </Row>
                            )
                        }))}
                        loading={loadingModal}
                        pagination={{
                            hideOnSinglePage: true
                        }}
                    />
                    <Modal
                        open={modal.open}
                        onCancel={() => {
                            setModal(defaultModal);
                            setModalUploadData(undefined);
                            setUploading(false);
                            setTypZmeny('');
                        }}
                        title={<TranslateText>{modal.type ? modal.type : ''}</TranslateText>}
                        footer={[
                            <Row
                                className="button-row"
                                style={{
                                    justifyContent: 'flex-end'
                                }}
                            >
                                <ButtonComponent
                                    id="zavritBtn"
                                    key={modal.text}
                                    buttontext="ZAVRIT"
                                    onClick={() => {
                                        setModal(defaultModal);
                                        setModalUploadData(undefined);
                                    }}
                                />
                                {modal.type === 'SRD_DETAIL_TL_VH' && typZmeny !== 'SRD_NOVA_ZPUSOB_XML' ? null : (
                                    <ButtonComponent
                                        id="odeslatBtn"
                                        loading={checking}
                                        buttontext="ODESLAT"
                                        disabled={
                                            modalUploadData ? potvrditFilelist.length < 1 : modalFilelist.length < 1
                                        }
                                        onClick={() => {
                                            uploadFile(modal.text);
                                            setChecking(true);
                                        }}
                                    />
                                )}
                            </Row>
                        ]}
                    >
                        {modal.type === 'SRD_DETAIL_TL_VH' && (
                            <SelectComponent
                                name="typUpravy"
                                labelParagraph="SRD_NOVA_ZPUSOB"
                                data={zpusobZadani}
                                isFormItem={true}
                                onChange={value => setTypZmeny(value)}
                                notRequired={true}
                            />
                        )}
                        <Row className="button-row" justify="center">
                            {modal.text === EkorpAkceButtonType.SRD_DETAIL_TL_VYPISODP && (
                                <ButtonComponent
                                    id="downloadVypisBtn"
                                    buttontext={modal.text}
                                    onClick={downloadVYPISODP}
                                    loading={loadingBtn}
                                />
                            )}
                            {modal.text === EkorpAkceButtonType.SRD_DETAIL_TL_VHODP && (
                                <ButtonComponent
                                    buttontext={modal.text}
                                    onClick={downloadVHODP}
                                    loading={loadingBtn}
                                    id="downloadVhBtn"
                                />
                            )}
                            {![
                                EkorpAkceButtonType.SRD_DETAIL_TL_VYPISODP,
                                EkorpAkceButtonType.SRD_DETAIL_TL_VHODP
                            ].includes(modal.text as EkorpAkceButtonType) && (
                                <Row justify="center">
                                    <Paragraph
                                        text={
                                            modal.type === 'SRD_DETAIL_TL_VH'
                                                ? typZmeny === 'SRD_NOVA_ZPUSOB_XML'
                                                    ? modal.text
                                                    : ''
                                                : modal.text
                                        }
                                    />
                                    <Row
                                        style={{
                                            marginTop: '10px'
                                        }}
                                    >
                                        {/* eslint-disable-next-line max-len */}
                                        {modal.type === 'SRD_DETAIL_TL_VH' &&
                                            typZmeny === 'SRD_NOVA_ZPUSOB_PRUVODCE' && (
                                                <ButtonComponent
                                                    id="novyBtn"
                                                    buttontext={'SRD_NOVA_ZPUSOB_PRUVODCE_BUTTON'}
                                                    style={{
                                                        marginRight: 10
                                                    }}
                                                    onClick={() => {
                                                        setLevel('idSRD', Number(id));
                                                        setLevel('prevPath', routePathEnum.DETAIL_KORPORATNI_AKCE);
                                                        navigateWorkaround(
                                                            // eslint-disable-next-line max-len
                                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.NOVA_UDALOST}${routePathEnum.PRUVODCE_SRD_VH}`
                                                        );
                                                    }}
                                                />
                                            )}
                                        {modal.type === 'SRD_DETAIL_TL_VH' &&
                                        typZmeny !== 'SRD_NOVA_ZPUSOB_XML' ? null : (
                                            <UploadComponent
                                                buttonId="xmlUploadBtn"
                                                accept=".xml"
                                                buttonLoading={uploading}
                                                fileList={modalUploadData ? potvrditFilelist : modalFilelist}
                                                onRemove={() => {
                                                    modalUploadData ? setPotvrditFilelist([]) : setModalFilelist([]);
                                                    setUploading(false);
                                                }}
                                                buttonDisabled={
                                                    modalUploadData
                                                        ? potvrditFilelist.length > 0
                                                        : modalFilelist.length > 0
                                                }
                                                onChange={uplData => {
                                                    if (uplData.fileList)
                                                        modalUploadData
                                                            ? setPotvrditFilelist(
                                                                  uplData.fileList
                                                                      .filter(f => f.status && f.status !== 'error')
                                                                      .map(e => {
                                                                          return {
                                                                              uid: e.uid!,
                                                                              name: e.name.normalize()!,
                                                                              fileName: e.fileName
                                                                                  ? e.fileName
                                                                                  : e.response
                                                                                    ? e.response.tempnazev
                                                                                    : ''!,
                                                                              type: e.type!,
                                                                              size: e.size!,
                                                                              status: e.status ? e.status : 'error'
                                                                          };
                                                                      })
                                                              )
                                                            : setModalFilelist(
                                                                  uplData.fileList
                                                                      .filter(f => f.status && f.status !== 'error')
                                                                      .map(e => {
                                                                          return {
                                                                              uid: e.uid!,
                                                                              name: e.name.normalize()!,
                                                                              fileName: e.fileName
                                                                                  ? e.fileName
                                                                                  : e.response
                                                                                    ? e.response.tempnazev
                                                                                    : ''!,
                                                                              type: e.type!,
                                                                              size: e.size!,
                                                                              status: e.status ? e.status : 'error'
                                                                          };
                                                                      })
                                                              );
                                                    uplData.event && setUploading(true);
                                                    if (uplData.file.response) {
                                                        setUploading(false);
                                                        uplData.fileList
                                                            .filter(f => f.status && f.status !== 'error')
                                                            .map(e =>
                                                                modalUploadData
                                                                    ? setPotvrditFilelist([
                                                                          {
                                                                              uid: e.uid,
                                                                              name: e.name.normalize(),
                                                                              fileName: e.fileName
                                                                                  ? e.fileName
                                                                                  : e.response
                                                                                    ? e.response.tempnazev
                                                                                    : '',
                                                                              type: e.type!,
                                                                              size: e.size!,
                                                                              status: e.status ? e.status : 'error'
                                                                          }
                                                                      ])
                                                                    : setModalFilelist([
                                                                          {
                                                                              uid: e.uid,
                                                                              name: e.name.normalize(),
                                                                              fileName: e.fileName
                                                                                  ? e.fileName
                                                                                  : e.response
                                                                                    ? e.response.tempnazev
                                                                                    : '',
                                                                              type: e.type!,
                                                                              size: e.size!,
                                                                              status: e.status ? e.status : 'error'
                                                                          }
                                                                      ])
                                                            );
                                                    }
                                                }}
                                            />
                                        )}
                                    </Row>
                                    {modal.type === 'SRD_DETAIL_TL_VH' && typZmeny !== 'SRD_NOVA_ZPUSOB_XML'
                                        ? null
                                        : uploadingError && (
                                              <p className="ant-typography extramessage-text">
                                                  <TranslateText>NEVALIDNI_XSD_SOUBOR</TranslateText>
                                              </p>
                                          )}
                                </Row>
                            )}
                        </Row>
                    </Modal>
                </>
            ) : null}
        </LoadingWrapper>
    );
};
